import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { BookOpen, ExternalLink, Search } from "lucide-react";
import { TopBar } from "@/components/layout/TopBar";
import { useHelpPages, type HelpPage } from "@/hooks/useHelp";
import { cn } from "@/lib/cn";

/**
 * Help & Training (2026-09-28) — the Confluence User Guide, readable
 * inside the planner. Confluence is still where it's edited (the PMO);
 * the worker mirrors it hourly so people without a Confluence login get
 * the same pages. Left: the sections. Right: the page. Search filters
 * sections by their text.
 */
export function Help() {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();
  const q = useHelpPages();
  const [search, setSearch] = useState("");

  const pages = useMemo(() => (q.data ?? []).slice().sort((a, b) => a.position - b.position), [q.data]);
  const current: HelpPage | undefined = useMemo(
    () => pages.find((p) => p.slug === slug) ?? pages.find((p) => p.section === 0) ?? pages[0],
    [pages, slug],
  );

  // Section list, narrowed by the search box (matches title or body text).
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return pages;
    return pages.filter((p) => (p.title + " " + stripTags(p.html)).toLowerCase().includes(needle));
  }, [pages, search]);

  // Intercept in-guide links (/help/<slug>) so they stay client-side.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const a = (e.target as HTMLElement)?.closest?.("a[href^='/help/']") as HTMLAnchorElement | null;
      if (!a) return;
      e.preventDefault();
      navigate(a.getAttribute("href")!);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [navigate]);

  // Scroll to the top of the article on page change.
  useEffect(() => { window.scrollTo({ top: 0 }); }, [current?.slug]);

  const html = useMemo(
    () => (current ? DOMPurify.sanitize(current.html, { ADD_ATTR: ["target"] }) : ""),
    [current],
  );

  return (
    <>
      <TopBar title="Help & Training" subtitle="How the planner works — creating projects, staffing people, reading the board. Edited in Confluence, mirrored here." />
      <div className="flex gap-6 p-8" data-testid="help">
        <aside className="w-64 shrink-0 space-y-3">
          <label className="flex items-center gap-2 rounded-lg border border-line bg-surface px-2.5 py-1.5 text-xs text-ink-muted">
            <Search className="h-3.5 w-3.5" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search the guide"
              aria-label="Search the guide"
              className="w-full bg-transparent text-sm text-ink outline-none placeholder:text-ink-muted"
            />
          </label>
          <nav className="space-y-0.5" aria-label="Guide sections">
            {visible.map((p) => (
              <Link
                key={p.page_id}
                to={`/help/${p.slug}`}
                data-testid="help-nav"
                className={cn(
                  "block rounded-lg px-3 py-2 text-sm transition-colors",
                  current?.slug === p.slug ? "bg-brand-navy-600 text-white" : "text-ink-3 hover:bg-raised hover:text-ink",
                )}
              >
                {p.section === 0 ? "Overview" : p.title}
              </Link>
            ))}
            {q.data && visible.length === 0 && (
              <p className="px-3 py-2 text-xs text-ink-muted">Nothing matches "{search}".</p>
            )}
          </nav>
        </aside>

        <main className="min-w-0 flex-1">
          {q.isLoading && (
            <div className="rounded-xl border border-line bg-surface p-12 text-center text-sm text-ink-muted">Loading the guide…</div>
          )}
          {q.isError && (
            <div className="rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-6 text-sm text-red-800 dark:text-red-300">
              Couldn't load the guide: {(q.error as Error).message}
            </div>
          )}
          {q.data && pages.length === 0 && (
            <div className="rounded-xl border border-line bg-surface p-12 text-center text-sm text-ink-muted" data-testid="help-empty">
              <BookOpen className="mx-auto mb-2 h-6 w-6" />
              The guide hasn't been mirrored from Confluence yet. It syncs every hour; an admin can trigger it from System.
            </div>
          )}
          {current && (
            <article className="rounded-xl border border-line bg-surface p-8">
              <div className="mb-6 flex flex-wrap items-start justify-between gap-3 border-b border-line pb-4">
                <h2 className="text-xl font-semibold text-ink" data-testid="help-title">
                  {current.section === 0 ? "Resource Planner User Guide" : current.title}
                </h2>
                <div className="flex items-center gap-3 text-xs text-ink-muted">
                  {current.synced_at && <span>Mirrored {new Date(current.synced_at).toLocaleString()}</span>}
                  {current.web_url && (
                    <a
                      href={current.web_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-brand-navy-600 dark:text-brand-navy-400 hover:underline"
                      data-testid="help-edit-link"
                    >
                      Edit in Confluence <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </div>
              <div className="help-doc" data-testid="help-body" dangerouslySetInnerHTML={{ __html: html }} />
            </article>
          )}
        </main>
      </div>
    </>
  );
}

function stripTags(html: string): string {
  return html.replace(/<[^>]+>/g, " ");
}
